import styled, { css } from 'styled-components';
import { Calendar } from '@solent-university/solent-icons';

import { Heading2 } from 'Elements/Typography/Typography.styled';

import { ReactComponent as BlackLinesSVG } from 'Public/graphics/clearing/lines--horizontal-long-black.svg';

export const StyledSection = styled.section`
    margin-bottom: 3rem;
`;

export const Wrapper = styled.div`
    ${({ theme }) => css`
        position: relative;
        display: flex;
        justify-content: center;
        background: ${theme.colors.secondary.tints[1].hex};
    `}
`;
export const Container = styled.div<{ $isClearingOpen: boolean }>`
    ${({ theme, $isClearingOpen }) => css`
        width: 100%;
        padding: ${$isClearingOpen ? '1rem 2.5rem 0' : '1rem 2.5rem 4rem'};

        @media ${theme.breakpoints.smallLarge.media} {
            padding: ${$isClearingOpen ? '1rem 2.5rem 0' : '1rem 2.5rem 4rem'};
        }

        @media ${theme.breakpoints.large.media} {
            padding: ${$isClearingOpen ? '1rem 2.5rem 0' : '1rem 2.5rem 5rem'};
        }
    `}
`;

export const Heading = styled.h3<{ $textDefined: boolean; $isClearingOpen: boolean }>`
    ${({ theme, $textDefined, $isClearingOpen }) => css`
        text-align: center;
        margin-top: ${$isClearingOpen ? '3.5rem' : '1.5rem'};
        margin-bottom: ${!$textDefined || !$isClearingOpen ? '1.5rem' : 'inherit'};
        font-size: 2rem;

        @media ${theme.breakpoints.large.media} {
            font-size: ${$isClearingOpen ? '2.5rem' : '2rem'};
        }
    `}
`;

export const Preheading = styled.div`
    ${({ theme }) => css`
        position: absolute;
        display: flex;
        align-items: center;
        text-align: center;
        flex-direction: column;
        justify-content: center;
        border-radius: 50%;
        left: calc(50% - 4rem);
        top: -4.25rem;
        padding: 0.5rem;
        width: 8rem;
        height: 8rem;
        background: ${theme.colors.primary.hex};

        @media ${theme.breakpoints.large.media} {
            left: calc(50% - 5rem);
            top: -6.25rem;
            width: 10rem;
            height: 10rem;
        }

        ${Heading2} {
            color: ${theme.colors.primaryContrast.hex};
            line-height: 1.2;
            margin: 1rem 0;
            font-size: 1.5rem;

            @media ${theme.breakpoints.large.media} {
                left: calc(50% - 5rem);
                font-size: 2rem;
            }
        }
    `}
`;

export const DecorativeLines = styled(BlackLinesSVG)`
    ${({ theme }) => css`
        position: absolute;
        display: none;
        z-index: 1;
        top: -0.75rem;
        right: 4rem;
        height: 1.5rem;
        width: 8.25rem;

        @media ${theme.breakpoints.large.media} {
            display: block;
        }
    `}
`;

export const PhoneWrapper = styled.div`
    ${({ theme }) => css`
        margin-top: 0.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1rem;

        @media ${theme.breakpoints.medium.media} {
            gap: 0.75rem;
        }

        @media ${theme.breakpoints.large.media} {
            display: none;
        }
    `}
`;

export const LinksList = styled.ul<{ $hasOpenDay: boolean }>`
    ${({ theme, $hasOpenDay }) => css`
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        justify-content: center;
        list-style: none;
        padding: 0;
        padding-bottom: 2.5rem;

        @media ${theme.breakpoints.smallLarge.media} {
            padding-bottom: 2.5rem;
        }

        @media ${theme.breakpoints.large.media} {
            padding-bottom: ${$hasOpenDay ? '3rem' : '2rem'};
            gap: 1.5rem;
        }
    `}
`;

export const StyledListItem = styled.li<{ $firstItem: boolean }>`
    ${({ theme, $firstItem }) => css`
        ${$firstItem &&
        css`
            display: none;

            @media ${theme.breakpoints.large.media} {
                display: list-item;
            }
        `}
    `}
`;

export const Text = styled.p`
    ${({ theme }) => css`
        text-align: center;
        margin-top: 0.5rem;
        margin-bottom: 0;
        color: inherit;

        :first-of-type {
            margin-bottom: 0.5rem;

            @media ${theme.breakpoints.large.media} {
                margin-bottom: 1rem;
            }
        }
    `}
`;

export const PostFooter = styled.div<{ $isClearingOpen: boolean }>`
    ${({ theme, $isClearingOpen }) => css`
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 1.5rem;
        width: 100%;
        max-width: 58rem;
        background: ${theme.colors.campaigns.clearing.secondary.hex};
        padding: 1rem 1.5rem 1.25rem;
        color: ${theme.colors.primaryContrast.hex};
        margin: ${$isClearingOpen ? '0' : '0 auto'};

        ${Text} {
            margin-bottom: 0;
        }

        @media ${theme.breakpoints.large.media} {
            gap: 1rem;
            justify-content: space-between;
        }
    `}
`;

export const PostFooterWrapper = styled.div`
    ${({ theme }) => css`
        padding: 0 1rem;
        width: 100%;
        max-width: 90%;
        margin: 0 auto;
        transform: translateY(-2rem);
        margin-bottom: -2rem;

        @media ${theme.breakpoints.large.media} {
            width: 60rem;
        }
    `}
`;

export const PostHeading = styled.h3`
    ${({ theme }) => css`
        color: inherit;
        margin-top: 0;
        text-align: center;

        @media ${theme.breakpoints.large.media} {
            text-align: left;
            font-size: 1.75rem;
        }
    `}
`;

export const CopyContainer = styled.div`
    ${({ theme }) => css`
        display: flex;
        flex-direction: column;
        width: 100%;

        @media ${theme.breakpoints.large.media} {
            width: auto;
        }
    `}
`;

export const Icon = styled(Calendar)`
    ${({ theme }) => css`
        width: 2.5rem;
        height: 2.5rem;
        margin-right: 1.25rem;

        @media ${theme.breakpoints.large.media} {
            width: 2.5rem;
            height: 2.5rem;
        }
    `}
`;

export const DateText = styled.p`
    ${({ theme }) => css`
        margin: 0;
        font-weight: 700;

        @media ${theme.breakpoints.smallLarge.media} {
            margin-right: 2.5rem;
        }

        @media ${theme.breakpoints.large.media} {
            margin: 0;
        }
    `}
`;

export const DateLabel = styled.span`
    ${({ theme }) => css`
        margin: 0;
        font-size: 1rem;

        @media ${theme.breakpoints.medium.media} {
            font-size: 1.125rem;
        }
    `}
`;
export const ClearingClosedLinkWrapper = styled.div`
    display: flex;
    justify-content: center;
`;

export const Location = styled.span`
    ${({ theme }) => css`
        background-color: ${theme.colors.campaigns.clearing.secondary.hex};
        color: ${theme.colors.campaigns.clearing.primaryContrast.hex};
        border: ${theme.commonStyles.brandPixel} solid
            ${theme.colors.campaigns.clearing.primaryContrast.hex};
        border-radius: 1rem;
        font-size: 1rem;
        padding: 0.125rem 0.5rem;
        text-align: center;
        display: inline-block;
        margin-top: 0.25rem;

        @media ${theme.breakpoints.large.media} {
            padding: 0.125rem 1rem;
        }
    `}
`;
